import React from 'react';
import MediaCover from '@opm/components/MediaCover';
import './index.scss';
import { DBO_V } from '@op/utils/format';
import { transLanguage } from '@op/utils';
import { LanguageType } from '@op/types/common';
import { DBOM_CONTENT } from '@opm/utils';
import ChannelLabel from '@op/components/ChannelLabel';
import CountryLabel from '@opm/components/CountryLabel';
import URLShare from '@opm/components/URLShare';
import moment from 'moment';
import { IMetricItem, DEFAULT_METRICS, resolveThumbnailUrl } from '../../constants';
import useIntls from '@opm/hooks/useIntls';

interface IXVideoCardProps {
  /** 单条数据 */
  item: any;
  /** 语言类型 */
  language?: LanguageType;
  /** 可配置指标列表 */
  metricsConfig?: IMetricItem[];
  /** 渠道名称（X 渠道数据无 channel_name 字段，默认 'twitter'） */
  channel?: string;
}

export default function XVideoCard({
  item,
  language = 'origin',
  metricsConfig = DEFAULT_METRICS,
  channel = 'twitter',
}: IXVideoCardProps) {
  const isVideo = item?.media_type === 'video';
  const thumbnailUrl = resolveThumbnailUrl(item);
  const hasImage = !!thumbnailUrl;
  const userName = item?.author_name || '';
  const contentText = DBOM_CONTENT.videoContent(language, item);
  const { intl, isEn } = useIntls();
  // 优先使用多语言标题，fallback 到 content_text
  const videoTitle = DBOM_CONTENT.videoTitle(language, item);
  const description = videoTitle || contentText;

  return (
    <div className="dbom-x-video-card">
      {/* 第一行：渠道图标 + 用户名 + 分享按钮 */}
      <div className="dbom-x-video-card-header">
        <ChannelLabel
          className="dbom-x-video-card-header-channel"
          channel_name={channel}
          replaceText={userName}
        />
        <URLShare
          url={item?.post_url}
          className="dbom-x-video-card-share"
          iconClassName="dbom-x-video-card-share-icon"
        />
      </div>

      {/* 第二行：国旗 | 语种 | 分类 | 时间（竖杠写在每个 item 的 ::after 上） */}
      <div className="dbom-x-video-card-meta">
        {item?.country && (
          <span className="dbom-x-video-card-meta-item dbom-x-video-card-meta-country">
            <CountryLabel
              country={item.country}
              iconStyle={{ width: 16, height: 16 }}
              showText={false}
            />
          </span>
        )}
        {item?.lang && (
          <span className="dbom-x-video-card-meta-item">
            {transLanguage(item.lang)}
          </span>
        )}
        {item?.category && (
          <span className="dbom-x-video-card-meta-item">
            {DBOM_CONTENT.categoryGlobal(isEn, item)}
          </span>
        )}
        {item?.publish_time && (
          <span className="dbom-x-video-card-meta-item">
            {moment(item.publish_time).format('YYYY-MM-DD HH:mm')}
          </span>
        )}
      </div>

      {/* 第三行：图片 + 描述 */}
      <div className="dbom-x-video-card-content">
        {hasImage ? (
          <>
            <MediaCover
              coverUrl={thumbnailUrl}
              duration={item.duration}
              showDuration={isVideo && !!item?.duration}
              className="dbom-x-video-card-content-thumb"
              coverClassName="dbom-x-video-card-content-thumb-img"
              durationClassName="dbom-x-video-card-content-duration"
            />
            <div className="dbom-x-video-card-content-desc">{description}</div>
          </>
        ) : (
          <div className="dbom-x-video-card-content-desc --full">
            {description}
          </div>
        )}
      </div>

      {/* 第四行：可配置指标（竖杠写在每个 item 的 border-right 上） */}
      <div className="dbom-x-video-card-metrics">
        {metricsConfig
          .filter(
            (metric) =>
              item?.[metric.key] != null && item?.[metric.key] !== undefined,
          )
          .map((metric) => {
            const formatFn = metric.format || DBO_V.reviews;
            return (
              <div key={metric.key} className="dbom-x-video-card-metrics-item">
                <div className="dbom-x-video-card-metrics-value">
                  {formatFn(item[metric.key])}
                </div>
                <div className="dbom-x-video-card-metrics-label">
                  {intl(metric.label)}
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
